/**
 * @file   src/Pages/LoyaltyProgramManagement.tsx
 * @brief  Loyalty Program Management page.
 */
import { React, useEffect, useOutletContext, useState, Container, Row, Col, Button, InputGroup } from '../utils/thirdpartyComponents';
import { AppContextType } from '../interfaces/generalInterface';
import { MessageToaster } from '../utils/toastUtil';
import './LoyaltyProgramManagement.scss';

const POINT_ACTIVITIES = [
  { id: 'newUserSignUp', label: 'New User Sign-Up' },
  { id: 'giftCardPurchase', label: 'Gift Card Purchase' },
  { id: 'memberSpend', label: '$1 spent by Members' },
  { id: 'nonMemberSpend', label: '$1 spent by Non Members' },
];

const INITIAL_POINTS: Record<string, string> = {
  newUserSignUp: '',
  giftCardPurchase: '',
  memberSpend: '',
  nonMemberSpend: '',
};

const INITIAL_REDEEM = {
  memberPointValue: '0.02',
  nonMemberPointValue: '0.01',
};

const digitsOnly = (value: string) => value.replace(/\D/g, '');

const decimalAmount = (value: string) => {
  const cleaned = value.replace(/[^\d.]/g, '');
  const match = cleaned.match(/^(\d*)(\.?)(\d{0,2})/);
  if (!match) return '';
  return `${match[1]}${match[2]}${match[3]}`;
};

const LoyaltyProgramManagement = () => {
  const { updateData } = useOutletContext<AppContextType>();
  const toaster = new MessageToaster();
  const [pointsEarned, setPointsEarned] = useState(INITIAL_POINTS);
  const [redeem, setRedeem] = useState(INITIAL_REDEEM);

  useEffect(() => {
    updateData({
      heading: 'Loyalty Program Management',
      url: '',
      showBackButton: false,
      showMainMenu: true,
      activeLink: 'loyalty-program-management',
    });
  }, []);

  const updatePoints = (id: string, value: string) => {
    setPointsEarned((prev) => ({ ...prev, [id]: digitsOnly(value) }));
  };

  const updateRedeem = (field: keyof typeof INITIAL_REDEEM, value: string) => {
    setRedeem((prev) => ({ ...prev, [field]: decimalAmount(value) }));
  };

  const handleCancel = () => {
    setPointsEarned(INITIAL_POINTS);
    setRedeem(INITIAL_REDEEM);
  };

  const handleSave = () => {
    toaster.toastSuccess('Loyalty program saved successfully.');
  };

  return (
    <div className="manage-carwash-container">
      <Container>
        <div className="cw-form-outer-wrap">
          <Row className="g-4">
            <Col lg={6}>
              <div className="cw-form-container h-100 loyalty-panel points-earned">
                <h5 className="f-bold mb-4">Points Earned</h5>
                <div className="loyalty-columns">
                  <span className="f-semi">Customer Activity</span>
                  <span className="f-semi">Points Earned</span>
                </div>
                {POINT_ACTIVITIES.map((activity) => (
                  <div className="loyalty-row" key={activity.id}>
                    <span className="f-medium">{activity.label}</span>
                    <div className="form-group cw-input mb-0">
                      <input
                        id={activity.id}
                        name={activity.id}
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        value={pointsEarned[activity.id]}
                        onChange={(event) => updatePoints(activity.id, event.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={6}>
              <div className="cw-form-container h-100 loyalty-panel redeem-points">
                <h5 className="f-bold mb-4">Redeem Points Configuration</h5>
                <div className="loyalty-row">
                  <span className="f-medium">Equivalent amount for 1 loyalty point for Members</span>
                  <div className="form-group cw-input mb-0">
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <input
                        id="memberPointValue"
                        name="memberPointValue"
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={redeem.memberPointValue}
                        onChange={(event) => updateRedeem('memberPointValue', event.target.value)}
                      />
                    </InputGroup>
                  </div>
                </div>
                <div className="loyalty-row">
                  <span className="f-medium">Equivalent amount for 1 loyalty point for Non Members</span>
                  <div className="form-group cw-input mb-0">
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <input
                        id="nonMemberPointValue"
                        name="nonMemberPointValue"
                        type="text"
                        inputMode="decimal"
                        className="form-control"
                        value={redeem.nonMemberPointValue}
                        onChange={(event) => updateRedeem('nonMemberPointValue', event.target.value)}
                      />
                    </InputGroup>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <div className="btn-sec d-flex justify-content-center">
            <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={handleCancel}>
              Cancel
            </Button>
            <Button title="Save" variant="primary" className="primary-btn" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default LoyaltyProgramManagement;
